/**
 * Panonun veri katmanı. `Dashboard`'a tek bir fetcher verilir; kaynak url'i
 * uca ve (varsa) fragment'e göre çözülür, ham yanıt grafiklerin beklediği
 * satır dizisine burada çevrilir.
 *
 * İki uç besliyor:
 *   /getBSHProdResults        ürün × periyot kırılımında talep/üretim/stok
 *   /getBSHStartStopResults   sipariş × periyot kırılımında hat olayları
 *
 * Aynı uç hem DETAY (grafikler) hem ÖZET (üstteki KPI ve pastalar) için
 * gerekiyor. İkisi ayrı kaynak olarak tanımlanır ama url'in yalnız fragment'i
 * (`#ozet`) farklıdır — fragment sunucuya gitmez, bu yüzden aşağıdaki önbellek
 * ağa tek istek çıkarır, dönüşüm ise kaynak başına ayrı çalışır.
 */
import { apiUrl } from "../../config";

/** Yanıt gelmezse kartta hata göster — sonsuz "Yükleniyor..." olmasın. */
const TIMEOUT_MS = 20000;

const AUTH = `Basic ${btoa(
  `${process.env.REACT_APP_USERNAME}:${process.env.REACT_APP_SECRET_KEY}`
)}`;

export const PROD_PATH = "/getBSHProdResults";
export const STARTSTOP_PATH = "/getBSHStartStopResults";
export const STOK_PATH = "/getDailyStockManagement";

/* ------------------------------------------------------------------ */
/* Yardımcılar                                                         */
/* ------------------------------------------------------------------ */

/** "06:20:00" → "06:20" (eksende saniye yer kaplamasın). */
const saat = (period) => String(period ?? "").slice(0, 5);

/**
 * "2026-09-11T08:11:27.803210" → "11.09.2026 08:11:27".
 * Date'e çevrilmez: uçtaki değer yerel saat kabul ediliyor, `new Date` ile
 * çevirmek tarayıcının saat dilimine göre kaydırırdı.
 */
const planTarihi = (raw) => {
  const s = String(raw ?? "");
  if (s.length < 19) return "";
  return `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)} ${s.slice(11, 19)}`;
};

/**
 * Renk → sütun adında kullanılan ek. Tanımsız renk atlanır.
 *
 * "Cast Iron" rapordaki küçük katların ikinci hücresi. API bugünkü veride
 * Cast Iron ürünlerini `color: "Beyaz"` ile döndürüyor, o yüzden kat boş
 * kalıyor — raporda da öyle. Renk alanı "Cast Iron" dönen günlerde kat
 * kendiliğinden dolar.
 */
const RENK_EKI = { Beyaz: "beyaz", Silver: "silver", "Cast Iron": "castiron" };
/** "U"/"Single" → sütun adı eki. */
const US_EKI = { U: "u", Single: "single" };

/**
 * Gövde ön tipi tanımdan okunur. Rapordaki hesaplanmış sütunun birebir
 * karşılığı:
 *   F = IF(CONTAINSSTRING(tanim, "F14"), "F14", "F23")
 * Yani "F14" geçmeyen her tanım F23 sayılır — "Cast Iron F14 Gövde Ön" F14'e,
 * "Beyaz F23 Gövde Ön" F23'e düşer.
 */
const onTipi = (tanim) => (String(tanim).includes("F14") ? "f14" : "f23");

/** Her satırda bulunan türetilmiş sütunlar — eksik kalan seri 0 olsun diye. */
const BOS_SUTUNLAR = {};
for (const renk of Object.values(RENK_EKI)) {
  BOS_SUTUNLAR[`${renk}_on_f14`] = 0;
  BOS_SUTUNLAR[`${renk}_on_f23`] = 0;
  BOS_SUTUNLAR[`${renk}_u_stok`] = 0;
  BOS_SUTUNLAR[`${renk}_single_stok`] = 0;
  BOS_SUTUNLAR[`${renk}_u_uretim`] = 0;
  BOS_SUTUNLAR[`${renk}_single_uretim`] = 0;
}

const say = (v) => Number(v) || 0;

/* ------------------------------------------------------------------ */
/* Vardiya penceresi                                                   */
/* ------------------------------------------------------------------ */

/**
 * Rapordaki `Vardiya` sütunu:
 *   IF(DateTimePeriod >= batch_date AND DateTimePeriod < batch_date + 8 saat,
 *      "Evet", "Hayır")
 * Grafikler yalnız "Evet" satırlarını çiziyor — plan üretildiği andan
 * itibaren sekiz saat.
 */
const VARDIYA_MS = 8 * 60 * 60 * 1000;

/** "2026-09-11" + "06:20:00" → epoch ms (yerel saat dilimine kaydırmadan). */
const anOf = (tarih, saatStr) => {
  const d = String(tarih ?? "");
  const t = String(saatStr ?? "");
  if (d.length < 10 || t.length < 5) return NaN;
  return Date.UTC(
    +d.slice(0, 4),
    +d.slice(5, 7) - 1,
    +d.slice(8, 10),
    +t.slice(0, 2),
    +t.slice(3, 5),
    +t.slice(6, 8) || 0
  );
};

/** Satırlardaki en güncel plan anı (epoch ms). Yoksa NaN. */
const batchAni = (rows) => {
  let en = "";
  for (const r of rows) {
    const b = r.order_batch?.batch_date;
    if (b && b > en) en = b;
  }
  return en ? anOf(en.slice(0, 10), en.slice(11, 19)) : NaN;
};

/* ------------------------------------------------------------------ */
/* İki ucun bağı                                                       */
/* ------------------------------------------------------------------ */

/**
 * Modeldeki ilişkinin birebir karşılığı:
 *   getBSHProdResults[Dizin adjusted] → getBSHStartStopResults[sequence]
 *   Dizin adjusted = FLOOR(Dizin / DISTINCTCOUNT(product), 1) + 1
 * `Dizin` ProdResults satırının uçtaki sırası (0'dan). Uç satırları periyot
 * bloklarıyla döndürüyor (her periyotta her ürün bir kez), yani k. blok
 * StartStop'un `sequence = k + 1` satırına bağlanır.
 *
 * Saat üzerinden eşleştirme YAPILMIYOR: iki ucun ızgarası bir dönem iki saat
 * kaymıştı, bağ sıra üzerinden olduğu için rapor bundan etkilenmiyordu.
 *
 * @returns her ProdResults satırı için bağlı StartStop satırı (yoksa null)
 */
const startStopBagi = (prod, ss) => {
  const urunSayisi = new Set(prod.map((r) => r.product)).size || 1;
  const bySeq = new Map(ss.map((r) => [r.sequence, r]));
  return prod.map((_, i) => bySeq.get(Math.floor(i / urunSayisi) + 1) ?? null);
};

/** Uç yanıtını satır dizisine çevirir. */
const dizi = (msg) => (Array.isArray(msg) ? msg : msg == null ? [] : [msg]);

/* ------------------------------------------------------------------ */
/* /getBSHProdResults — detay                                          */
/* ------------------------------------------------------------------ */

/**
 * Grafikler seriyi tek bir sütundan okur; uç ise ürün başına satır döndürür.
 * Bu yüzden her satır, ait olduğu seriye (renk × tip) karşılık gelen sütuna
 * yazılır, kalan sütunlar 0 kalır. Grafik `rollup: "sum"` ile periyoda göre
 * topladığında seriler doğru çıkar.
 *
 * NOT: "Cast Iron ..." ürünlerinin `color` alanı uçta "Beyaz" geliyor; Power
 * BI raporundaki Beyaz serileri de bu ürünleri içeriyor — ek bir ayıklama
 * YAPILMIYOR, aksi hâlde stok eğrisi rapordan sapar.
 */
const prodRows = async (msg, getir) => {
  const tum = dizi(msg);

  // Vardiya süzgeci. `Vardiya` StartStop tablosunda hesaplandığı için her
  // üretim satırının anı, bağlı olduğu StartStop satırının anıdır. Plan anı
  // okunamazsa süzme yapılmaz — eksik bir alan yüzünden grafiklerin tamamen
  // boşalmasındansa geniş aralık gösterilir.
  const batch = batchAni(tum);
  let rows = tum;
  if (!Number.isNaN(batch)) {
    const bag = startStopBagi(tum, dizi(await getir(STARTSTOP_PATH)));
    rows = tum.filter((r, i) => {
      const ss = bag[i];
      if (!ss) return false;
      const an = anOf(ss.date, ss.period);
      return an >= batch && an < batch + VARDIYA_MS;
    });
  }

  return rows
    .map((r) => {
      const satir = { ...r, ...BOS_SUTUNLAR, zaman: saat(r.period) };
      const renk = RENK_EKI[r.color];
      if (!renk) return satir;

      // Gövde ön stoğu: F sütunu (F14 / F23) × renk.
      if (r.govde_and_govde_on === "Gövde Ön") {
        satir[`${renk}_on_${onTipi(r.tanim)}`] = say(r.inventory);
        return satir;
      }

      // Gövde üretim/stok: rapordaki dört hesaplanmış sütunun karşılığı —
      //   UGovdeInventory / UGovdeProduction        (u_single = "U")
      //   SingleGovdeInventory / SingleGovdeProduction (u_single = "Single")
      // ikisi de govde_and_govde_on = "Gövde" koşuluyla; koşul tutmayan satır
      // 0 kalır. Renk kırılımı görsel süzgecinden geliyor, burada sütun adına
      // giriyor.
      if (r.govde_and_govde_on === "Gövde") {
        const us = US_EKI[r.u_single];
        if (us) {
          satir[`${renk}_${us}_stok`] = say(r.inventory);
          satir[`${renk}_${us}_uretim`] = say(r.production);
        }
      }
      return satir;
    })
    .sort((a, b) => String(a.period).localeCompare(String(b.period)));
};

/* ------------------------------------------------------------------ */
/* /getBSHProdResults — özet (tek satır)                               */
/* ------------------------------------------------------------------ */

/**
 * "U / Single ölçütüne göre Üretim" pastası: `production` toplamı, `u_single`
 * kırılımında, yalnız gövde satırları (rapordaki görsel süzgeci
 * `govde_and_govde_on = "Gövde"`).
 */
const prodOzet = (msg) => {
  const rows = Array.isArray(msg) ? msg : [msg];
  const ozet = { u_uretim: 0, single_uretim: 0 };

  for (const r of rows) {
    if (r.govde_and_govde_on !== "Gövde") continue;
    const uretim = say(r.production);
    if (r.u_single === "U") ozet.u_uretim += uretim;
    else if (r.u_single === "Single") ozet.single_uretim += uretim;
  }

  return [
    {
      ...ozet,
      u_pay: pay(ozet.u_uretim, ozet.single_uretim),
      single_pay: pay(ozet.single_uretim, ozet.u_uretim),
    },
  ];
};

/**
 * Pasta dilimlerinin payı. Kütüphanenin `percent` biçimi 0-1 arası bir KESİR
 * bekler (`Intl` style:"percent"); pay burada hesaplanınca dilim oranı da
 * etiket de tek alandan gelir.
 */
function pay(a, b) {
  return a + b ? a / (a + b) : 0;
}

/* ------------------------------------------------------------------ */
/* /getDailyStockManagement — özet (tek satır)                         */
/* ------------------------------------------------------------------ */

/**
 * Üst şeritteki ilk iki kart. Başlıkları "Gövde Ön Üretim" ve "Gövde Üretim"
 * ama ölçtükleri şey ÜRETİM DEĞİL STOK: raporda ikisi de bu ucun
 * `stock_amount` alanını topluyor, aralarındaki tek fark
 * `material_description` içinde "Gövde Ön" geçip geçmemesi.
 */
const stokOzet = (msg) => {
  const rows = Array.isArray(msg) ? msg : [msg];
  const ozet = { stok_govde_on: 0, stok_govde: 0 };

  for (const r of rows) {
    const miktar = say(r.stock_amount);
    if (String(r.material_description ?? "").includes("Gövde Ön")) {
      ozet.stok_govde_on += miktar;
    } else {
      ozet.stok_govde += miktar;
    }
  }

  return [ozet];
};

/* ------------------------------------------------------------------ */
/* /getBSHStartStopResults — özet (tek satır)                          */
/* ------------------------------------------------------------------ */

/**
 * Duruş sayısı, ilk renk değişimi, renk pastası ve planın üretildiği an.
 *
 * Duruş: raporda `SUM(stop length)`, `stop length` de "first planned stop
 * boş değilse 1" demek. O hesaplanmış sütun uçta yok; duruş satırlarının
 * karşılığı `siparis` metninde "Duruş" geçenler ("Planlı Duruş",
 * "Rework + Duruş") ve aynı satırlarda `renk` de boş geliyor. İki sayım da
 * rapordaki değeri veriyor.
 *
 * Renk değişimi: `is_color_change` işaretli EN ERKEN satırın zamanı,
 * rapordaki gibi "YYYY-MM-DD HH:mm:ss" biçiminde; hiç yoksa "Renk Değişimi
 * Yok".
 *
 * Renk pastası: rapor bu uçtaki satırları `renk` kırılımında SAYIYOR, üretim
 * miktarını toplamıyor — boş renkli (duruş) satırlar süzgeçle eleniyor.
 * Miktar toplamı rapordaki yüzdeyi vermiyor.
 */
const startStopOzet = (msg) => {
  const rows = Array.isArray(msg) ? msg : [msg];

  let durus_sayisi = 0;
  let ilk_degisim = null;
  let batch = "";
  let beyaz_adet = 0;
  let silver_adet = 0;

  for (const r of rows) {
    if (String(r.siparis ?? "").includes("Duruş")) durus_sayisi += 1;

    if (r.is_color_change) {
      // Rapordaki `datetime` sütunu = tarih + periyot.
      const an = `${r.date} ${r.period}`;
      if (!ilk_degisim || an < ilk_degisim) ilk_degisim = an;
    }

    if (r.renk === "Beyaz") beyaz_adet += 1;
    else if (r.renk === "Silver") silver_adet += 1;

    const b = r.order_batch?.batch_date;
    if (b && b > batch) batch = b;
  }

  return [
    {
      durus_sayisi,
      ilk_renk_degisim: ilk_degisim ?? "Renk Değişimi Yok",
      plan_tarihi: planTarihi(batch),
      beyaz_adet,
      silver_adet,
      beyaz_pay: pay(beyaz_adet, silver_adet),
      silver_pay: pay(silver_adet, beyaz_adet),
    },
  ];
};

/* ------------------------------------------------------------------ */
/* Üretim / Gövde Stok / Gövde Ön Stok sekmeleri                       */
/* ------------------------------------------------------------------ */

/*
 * Bu üç sayfada Vardiya süzgeci YOK — rapor planın tüm ufkunu gösteriyor.
 * Satırlar ham kalır (ürün × periyot); tablolar ve grafikler gruplamayı
 * slicer'lar uygulandıktan sonra kendisi yapar. Slicer'ların süzdüğü alanlar
 * (`product`, `color`, `u_single`, `govde_and_govde_on`) bu yüzden her satırda
 * durur.
 */

/** "2026-10-12" → "12.10.2026" (Date'e çevirmeden, saat dilimi kaymasın). */
const gunAyYil = (tarih) => {
  const s = String(tarih ?? "");
  return s.length >= 10 ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}` : s;
};

/**
 * Rapordaki `h:nn` biçimi — saatte baştaki sıfır yok ("7:20").
 * @param ekDakika üzerine eklenecek dakika (Bitiş = periyot + 20 dk)
 */
const saatKisa = (period, ekDakika = 0) => {
  const s = String(period ?? "");
  if (s.length < 5) return "";
  const dk = (+s.slice(0, 2) * 60 + +s.slice(3, 5) + ekDakika) % (24 * 60);
  return `${Math.floor(dk / 60)}:${String(dk % 60).padStart(2, "0")}`;
};

/** Sayfaların ortak satırı — ProdResults satırı, rapordaki adlarla. */
const sayfaSatiri = (r) => ({
  id: r.id,
  order_nr: r.order_nr,
  tarih: gunAyYil(r.date),
  saat: r.period,
  zaman: saat(r.period),
  // Slicer'ın süzdüğü sütunlar — değerleri uçtaki gibi bırakılır.
  product: String(r.product ?? ""),
  color: r.color,
  u_single: r.u_single,
  govde_and_govde_on: r.govde_and_govde_on,
  tanim: r.tanim,
  F: onTipi(r.tanim) === "f14" ? "F14" : "F23",
  demand: say(r.demand),
  production: say(r.production),
  inventory: say(r.inventory),
  // Modelde `work_in_process` → "Safety Stock" olarak yeniden adlandırılıyor.
  emniyet: say(r.work_in_process),
});

/**
 * "Üretim" sekmesi: ProdResults satırına bağlı StartStop satırının alanları
 * eklenir (sipariş metni, hat üretimi, HDS stoğu, periyodun bitişi).
 *
 * @param yalnizGovde pivotun görsel süzgeci `govde_and_govde_on = "Gövde"`
 */
const uretimSatirlari = (yalnizGovde) => async (msg, getir) => {
  const prod = dizi(msg);
  const bag = startStopBagi(prod, dizi(await getir(STARTSTOP_PATH)));
  const out = [];
  prod.forEach((r, i) => {
    if (yalnizGovde && r.govde_and_govde_on !== "Gövde") return;
    const ss = bag[i];
    out.push({
      ...sayfaSatiri(r),
      // Sıralama ve gruplama anahtarı: tarih + saat.
      periyot: `${r.date} ${r.period}`,
      ss_id: ss?.id ?? "",
      ss_order_nr: ss?.order_nr ?? "",
      ss_tarih: gunAyYil(ss?.date),
      baslangic: r.period,
      bitis: ss ? saatKisa(ss.period, 20) : "",
      siparis: ss?.siparis ?? "",
      ss_uretim: ss ? say(ss.uretim) : "",
      hds: ss ? say(ss.total_hds_stok) : "",
    });
  });
  return out;
};

/** Sayfa süzgeci `govde_and_govde_on = tip` olan stok sekmeleri. */
const stokSatirlari = (tip) => (msg) =>
  dizi(msg)
    .filter((r) => r.govde_and_govde_on === tip)
    .map((r) => {
      const s = sayfaSatiri(r);
      // Gövde Ön grafiği F sütununu seri yapıyor; seri başına sütun açılır,
      // grafik periyoda göre toplayınca F14 / F23 eğrileri çıkar.
      s.on_f14 = s.F === "F14" ? s.inventory : 0;
      s.on_f23 = s.F === "F23" ? s.inventory : 0;
      return s;
    });

/**
 * "Üretim" sekmesinin sol kartları (tek satır).
 *
 * İkisi de StartStop tablosunun TAMAMI üzerinden EN KÜÇÜK değeri alıyor —
 * ilişki tek yönlü olduğu için slicer'lar bu kartları etkilemez:
 *   ilk_tarih      MIN(date)                    biçim dd/mm/yyyy
 *   single_gormeme MIN([single görmeme süresi]) biçim hh:nn
 *     single görmeme süresi = TIME(FLOOR(single_gormeme / 3), 20 * MOD(…, 3), 0)
 *     yani single_gormeme 20 dakikalık periyot sayısı.
 */
const uretimKartlari = (msg) => {
  const rows = dizi(msg);
  let ilk = "";
  let enAz = null;
  for (const r of rows) {
    if (r.date && (!ilk || r.date < ilk)) ilk = r.date;
    const n = Number(r.single_gormeme);
    if (Number.isFinite(n) && (enAz == null || n < enAz)) enAz = n;
  }
  const dk = enAz == null ? null : enAz * 20;
  return [
    {
      ilk_tarih: gunAyYil(ilk),
      single_gormeme:
        dk == null
          ? ""
          : `${String(Math.floor(dk / 60) % 24).padStart(2, "0")}:${String(dk % 60).padStart(2, "0")}`,
    },
  ];
};

/* ------------------------------------------------------------------ */
/* Arz / Talep sekmesi                                                 */
/* ------------------------------------------------------------------ */

/**
 * Malzemenin dört alandan hangisine düştüğü.
 *
 * Renk `color` alanından OKUNMUYOR: uç Cast Iron ürünlerini "Beyaz"
 * döndürüyor, ama Cast Iron boyalı bir gövde — renkli tarafta sayılır.
 * Tanımda "Silver" ya da "Cast Iron" geçen her ürün renkli.
 */
const arzGrubu = (r) => {
  const renkli = /Silver|Cast Iron/.test(String(r.tanim));
  const on = r.govde_and_govde_on === "Gövde Ön";
  return `${renkli ? "renkli" : "beyaz"}-${on ? "on" : "govde"}`;
};

/**
 * Malzeme başına plan ufku boyunca arz ve talep (tek grup):
 *   uretim  Σ production
 *   stok    ilk periyottaki inventory — planın başlangıç stoğu
 *   talep   Σ demand (anamontaj tüketimi)
 *   kritik  uretim + stok < talep
 *   stok_son son periyottaki inventory — ufuk sonu stoğu (yalnız araç ipucu)
 * Üretimi de talebi de olmayan malzemeler gösterilmez. Sıra: arz büyükten
 * küçüğe.
 *
 * Vardiya süzgeci YOK — mailde gelen örnek grafikteki sayılar planın tüm
 * ufkunun toplamıyla örtüşüyor.
 */
const arzTalep = (grup) => (msg) => {
  const urunler = new Map();
  for (const r of dizi(msg)) {
    if (arzGrubu(r) !== grup) continue;
    let u = urunler.get(r.product);
    if (!u) {
      u = {
        product: String(r.product ?? ""),
        // Eksende "8001340261 Beyaz F14 Ön" — "Gövde" sözcüğü alanın
        // başlığında zaten var.
        malzeme: `${r.product} ${String(r.tanim ?? "").replace(/\s*Gövde\s*/g, " ").trim()}`,
        uretim: 0,
        stok: say(r.inventory),
        talep: 0,
      };
      urunler.set(r.product, u);
    }
    u.uretim += say(r.production);
    u.talep += say(r.demand);
    // Satırlar periyot sırasıyla geliyor; son yazılan, ufkun son periyodu.
    u.stok_son = say(r.inventory);
  }
  return [...urunler.values()]
    .filter((u) => u.uretim > 0 || u.talep > 0)
    .map((u) => ({ ...u, arz: u.uretim + u.stok, kritik: u.uretim + u.stok < u.talep }))
    .sort((a, b) => b.arz - a.arz);
};

/** Epoch ms (UTC alanları yerel saat sayılarak) → "12.10.2026 07:00". */
const anYaz = (ms) => {
  const d = new Date(ms);
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getUTCDate())}.${p(d.getUTCMonth() + 1)}.${d.getUTCFullYear()} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
};

/** Periyot uzunluğu — ızgara 20 dakikalık. */
const PERIYOT_MS = 20 * 60 * 1000;

/**
 * Arz / Talep sekmesinin üst şeridi (tek satır): grafiklerdeki sayıların
 * hangi zamana ait olduğu.
 *   plan_tarihi  planın üretildiği an (order_batch.batch_date)
 *   ufuk         ilk periyodun başı → son periyodun sonu (+20 dk)
 */
const arzKapsam = (msg) => {
  const rows = dizi(msg);
  let ilk = Infinity;
  let son = -Infinity;
  for (const r of rows) {
    const an = anOf(r.date, r.period);
    if (Number.isNaN(an)) continue;
    if (an < ilk) ilk = an;
    if (an > son) son = an;
  }
  const b = rows.reduce((en, r) => {
    const v = r.order_batch?.batch_date;
    return v && v > en ? v : en;
  }, "");
  if (!Number.isFinite(ilk)) return [{ plan_tarihi: planTarihi(b), ufuk: "" }];

  const bitis = son + PERIYOT_MS;
  const saatSayisi = Math.round((bitis - ilk) / 3600000);
  return [
    {
      plan_tarihi: planTarihi(b),
      ufuk: `${anYaz(ilk)} → ${anYaz(bitis)} (${saatSayisi} saat)`,
    },
  ];
};

/* ------------------------------------------------------------------ */
/* Kaynak çözümleme                                                    */
/* ------------------------------------------------------------------ */

/**
 * `${yol}#${fragment}` → dönüşüm. Fragment yoksa detay dönüşümü geçerli.
 * Dönüşüm `(msg, getir)` alır; `getir(yol)` başka bir ucun yanıtını aynı
 * sorgu dizesiyle (aynı önbellekten) getirir — iki ucu birleştiren sayfalar
 * için.
 */
const TRANSFORMS = {
  [PROD_PATH]: prodRows,
  [`${PROD_PATH}#ozet`]: prodOzet,
  [`${STARTSTOP_PATH}#ozet`]: startStopOzet,
  [`${STOK_PATH}#ozet`]: stokOzet,
  [`${PROD_PATH}#uretim`]: uretimSatirlari(false),
  [`${PROD_PATH}#uretim-govde`]: uretimSatirlari(true),
  [`${STARTSTOP_PATH}#uretim`]: uretimKartlari,
  [`${PROD_PATH}#govde`]: stokSatirlari("Gövde"),
  [`${PROD_PATH}#govde-on`]: stokSatirlari("Gövde Ön"),
  [`${PROD_PATH}#arz-kapsam`]: arzKapsam,
  [`${PROD_PATH}#arz-beyaz-on`]: arzTalep("beyaz-on"),
  [`${PROD_PATH}#arz-beyaz-govde`]: arzTalep("beyaz-govde"),
  [`${PROD_PATH}#arz-renkli-on`]: arzTalep("renkli-on"),
  [`${PROD_PATH}#arz-renkli-govde`]: arzTalep("renkli-govde"),
};

/**
 * Ağ önbelleği: anahtar url'in fragment'siz hâli. Aynı ucun detay ve özet
 * kaynakları tek yanıtı paylaşır. `refreshKey` sorgu dizesinde taşındığı için
 * her yenileme ayrı bir kuşak; eski kuşaklar `tazele` başarılı olunca
 * temizlenir.
 */
const cache = new Map();

function istek(path, query) {
  const url = `${apiUrl}${path}${query}`;
  if (cache.has(url)) return cache.get(url);

  const p = cagir(url, path).catch((err) => {
    // Hatalı yanıt önbellekte kalırsa yeniden denenemez.
    cache.delete(url);
    throw err;
  });
  cache.set(url, p);
  return p;
}

async function cagir(url, path) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);

  let res;
  try {
    res = await fetch(url, {
      method: "GET",
      cache: "no-store",
      headers: { Accept: "application/json", Authorization: AUTH },
      signal: ctrl.signal,
    });
  } catch (err) {
    throw new Error(
      err.name === "AbortError"
        ? `${path} → ${TIMEOUT_MS / 1000} sn içinde yanıt vermedi`
        : `${path} → ağ hatası: ${err.message}`
    );
  } finally {
    clearTimeout(timer);
  }

  if (!res.ok) throw new Error(`${path} → HTTP ${res.status}`);
  const json = await res.json();
  return SAATI_KAYAN_UCLAR.has(path) ? saatiKaydir(json) : json;
}

/* ------------------------------------------------------------------ */
/* Periyot saatlerinin +3 saat düzeltmesi                              */
/* ------------------------------------------------------------------ */

/**
 * Plan satırlarının `date` / `period` değerleri fabrika saatinin 3 saat
 * gerisinde geliyor; Asma ekranındaki planla aynı saati göstermek için
 * pano bunları 3 saat ileri alır. Düzeltme ağ yanıtında, önbelleğe
 * girmeden BİR KEZ yapılır — bütün sekmeler, Vardiya penceresi ve iki ucun
 * birleşimi aynı kaydırılmış saati görür.
 *
 * `order_batch.batch_date` kaydırılmaz: o zaten fabrika saati.
 *
 * Backend/model tarafı düzeltilirse bu sabit 0 yapılmalı, yoksa saatler
 * bu sefer 3 saat ileri kayar.
 */
const SAAT_DUZELTMESI_MS = 3 * 60 * 60 * 1000;

/** Saatleri kaydırılan uçlar — ikisi de ızgara satırı (`date` + `period`). */
const SAATI_KAYAN_UCLAR = new Set([PROD_PATH, STARTSTOP_PATH]);

const p2 = (n) => String(n).padStart(2, "0");

/** "2026-10-12" + "22:20:00" → kaydırılmış { date, period }. Okunamazsa null. */
function kaydir(tarih, saatStr) {
  const an = anOf(tarih, saatStr);
  if (Number.isNaN(an)) return null;
  const d = new Date(an + SAAT_DUZELTMESI_MS);
  return {
    date: `${d.getUTCFullYear()}-${p2(d.getUTCMonth() + 1)}-${p2(d.getUTCDate())}`,
    period: `${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}:${p2(d.getUTCSeconds())}`,
  };
}

function saatiKaydir(json) {
  if (!SAAT_DUZELTMESI_MS) return json;
  const satir = (r) => {
    if (!r || typeof r !== "object") return r;
    const k = kaydir(r.date, r.period);
    if (!k) return r;
    const out = { ...r, date: k.date, period: k.period };
    // Toplama saati de aynı ızgarada; tarihi yok, periyodun tarihiyle kaydırılır.
    if (r.toplama_period) {
      const t = kaydir(r.date, r.toplama_period);
      if (t) out.toplama_period = t.period;
    }
    return out;
  };
  if (Array.isArray(json)) return json.map(satir);
  if (Array.isArray(json?.message)) return { ...json, message: json.message.map(satir) };
  return json;
}

/** Panonun beslendiği tüm uçlar — yenilemede hepsi birlikte çekilir. */
const TUM_UCLAR = [PROD_PATH, STARTSTOP_PATH, STOK_PATH];

/**
 * Yeni bir yenileme kuşağını arka planda hazırlar: üç uç `?_t=<anahtar>`
 * ile çekilip önbelleğe alınır. Hepsi gelirse eski kuşaklar silinir ve söz
 * tamamlanır; pano ancak bundan SONRA yeni anahtara geçirilmeli.
 *
 * Neden önce çekiliyor: kütüphane url değişince bütün kartları "yükleniyor"
 * durumuna çekip veriyi baştan istiyor. Yanıt hazırsa bu an bir kare sürer,
 * ekran boşalmaz; bir uç hata verirse söz reddedilir, pano eski anahtarda
 * — eski veriyle — kalır, kartlar hata göstermez.
 */
export async function tazele(refreshKey) {
  const query = `?_t=${refreshKey}`;
  await Promise.all(TUM_UCLAR.map((p) => istek(p, query)));
  for (const url of cache.keys()) {
    if (!url.endsWith(query)) cache.delete(url);
  }
}

/** `Dashboard`'ın çağırdığı tek giriş noktası. */
export async function dashboardFetcher(source) {
  const [yolVeSorgu, fragment = ""] = String(source.url).split("#");
  const [path, sorgu = ""] = yolVeSorgu.split(/(\?.*)/);

  const json = await istek(path, sorgu);
  const anahtar = fragment ? `${path}#${fragment}` : path;
  const transform = TRANSFORMS[anahtar];
  const msg = json?.message ?? json;

  const getir = async (digerYol) => {
    const j = await istek(digerYol, sorgu);
    return j?.message ?? j;
  };

  if (transform) return transform(msg, getir);
  return dizi(msg);
}
